import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Database, DollarSign, Shield } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { collection, addDoc, getDocs, doc, updateDoc, query, where, orderBy, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import AccountSettings from './account/AccountSettings';
import { useAuth } from '../contexts/AuthContext';
import { clearUser } from '../store/slices/authSlice';
import { ThemeToggle } from './ThemeToggle';
// Main App Component
const VeniceMarketplace = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { signOut } = useAuth();
  
  // Get tab from URL query param if it exists
  const urlParams = new URLSearchParams(location.search);
  const tabParam = urlParams.get('tab');
  
  const [activeTab, setActiveTab] = useState(tabParam || 'home');
  
  // Update URL when tab changes
  const handleTabChange = (tab) => {
    // Always allow tab change, even if data is loading
    setLoading(false); // Reset loading state if it's hung
    setActiveTab(tab);
    navigate(`/?tab=${tab}`, { replace: true });
    
    // If switching to marketplace tab, try to fetch offers again if needed
    if (tab === 'marketplace' && offers.length === 0) {
      fetchOffers().catch(error => {
        console.error("Error re-fetching offers:", error);
        toast.error("Failed to load marketplace data. Please try again.");
      });
    }
  };
  
  const [totalVcuAllocation, setTotalVcuAllocation] = useState(8500);
  const [accountCredit, setAccountCredit] = useState(25);  // TESTING ONLY: Default $25 credit - REMOVE IN PRODUCTION
  const [paymentMethod, setPaymentMethod] = useState('credit'); // 'credit' or 'card'
  const [showReplenishModal, setShowReplenishModal] = useState(false);
  const [replenishAmount, setReplenishAmount] = useState(100);
  const [offers, setOffers] = useState([
    // Initially empty - will be populated from Firebase
  ]);
  
  const [newOffer, setNewOffer] = useState({
    dailyVcu: 1000,
    minPeriod: 7,
    pricePerDay: 5.00,
    apiKey: '',
  });
  
  const [userOffers, setUserOffers] = useState([]);
  const [userBorrows, setUserBorrows] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [borrowDays, setBorrowDays] = useState(7);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutDetails, setCheckoutDetails] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Add this helper function at the beginning of the component
  const formatNumber = (value, decimalPlaces = 2) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "0.00";
    }
    return Number(value).toFixed(decimalPlaces);
  };
  
  // Calculate total offered VCUs (including those that are currently rented)
  const calculateTotalOfferedVCUs = () => {
    return userOffers.reduce((sum, offer) => sum + offer.dailyVcu, 0);
  };
  
  // eslint-disable-next-line no-unused-vars
  const calculateAvailableVCUs = () => {
    return totalVcuAllocation - calculateTotalOfferedVCUs();
  };
  
  // Add this function near the top with other helper functions
  const fetchProviderName = async (providerEmail) => {
    try {
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", providerEmail)
      );
      const userSnapshot = await getDocs(usersQuery);
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        return userData.providerName || userData.displayName || providerEmail;
      }
      return providerEmail;
    } catch (error) {
      console.error("Error fetching provider name:", error);
      return providerEmail;
    }
  };
  
  // Update the fetchOffers function
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const offersQuery = query(
        collection(db, "offers"),
        where("status", "==", "active")
      );
      
      const querySnapshot = await getDocs(offersQuery);
      const offersData = await Promise.all(querySnapshot.docs.map(async doc => {
        const data = doc.data();
        let providerName = 'Unknown Provider';
        try {
          providerName = await fetchProviderName(data.provider);
        } catch (providerError) {
          console.error(`Error fetching provider name for ${data.provider}:`, providerError);
          // Continue with default name if provider name fetch fails
        }
        return {
          id: doc.id,
          ...data,
          providerName
        };
      }));
      
      console.log('Fetched offers:', offersData);
      setOffers(offersData);
    } catch (error) {
      console.error("Error fetching offers:", error);
      setOffers([]);
      toast.error("Error loading marketplace data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  // Add this helper function at the top of the component
  const isOfferExpired = (offer) => {
    if (offer.status !== 'cancelled' && offer.status !== 'cancelling') return false;
    if (!offer.cancellationDate) return true;
    return new Date() > new Date(offer.cancellationDate);
  };
  
  const calculateOfferEarnings = (offer) => {
    if (!offer.rentals || !Array.isArray(offer.rentals)) return 0;
    
    return offer.rentals.reduce((total, rental) => {
      const startDate = new Date(rental.startDate);
      const now = new Date();
      const endDate = rental.status === 'cancelled' ? 
        new Date(rental.cancellationDate) : 
        new Date(rental.endDate);
      
      // For same day rentals or first day of longer rentals
      if (startDate.toDateString() === now.toDateString() || 
          (now > startDate && now < endDate)) {
        // Add the first day's earnings to escrow immediately
        return total + offer.pricePerDay;
      }
      
      // For completed rentals
      if (now > endDate) {
        const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        return total + (days * offer.pricePerDay);
      }
      
      return total;
    }, 0);
  };
  
  // Fetch user's offers and rentals
  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated || !user?.uid) {
      setUserOffers([]);
      setUserBorrows([]);
      return;
    }
    
    let loadingTimeout;
    
    try {
      // Set a safety timeout
      loadingTimeout = setTimeout(() => {
        console.log('User data loading timeout reached');
        setUserOffers([]);
        setUserBorrows([]);
      }, 10000); // 10 seconds timeout
      
      // Fetch user's offers
      const userOffersQuery = query(
        collection(db, "offers"),
        where("provider", "==", user.email)
      );
      
      const userOffersSnapshot = await getDocs(userOffersQuery);
      let userOffersData = userOffersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        rentals: []
      }));
      
      // Fetch rentals for each offer - with added safety
      const offerPromises = userOffersData.map(async (offer) => {
        try {
          const rentalsQuery = query(
            collection(db, "rentals"),
            where("offerId", "==", offer.id)
          );
          const rentalsSnapshot = await getDocs(rentalsQuery);
          offer.rentals = rentalsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          offer.totalEarnings = calculateOfferEarnings(offer);
          return offer;
        } catch (error) {
          console.error(`Error fetching rentals for offer ${offer.id}:`, error);
          offer.rentals = [];
          offer.totalEarnings = 0;
          return offer;
        }
      });
      
      userOffersData = await Promise.all(offerPromises);
      
      // Filter out expired cancelled offers
      userOffersData = userOffersData.filter(offer => !isOfferExpired(offer));
      
      setUserOffers(userOffersData);
      
      // Fetch user's rentals
      try {
        // Create an array of possible consumer values to check
        const possibleConsumerValues = [user.email, user.uid];
        const userRentals = [];
        
        // Query for rentals where consumer is email
        const userRentalsEmailQuery = query(
          collection(db, "offers"),
          where("status", "==", "locked"),
          where("consumer", "==", user.email)
        );
        
        const emailRentalsSnapshot = await getDocs(userRentalsEmailQuery);
        emailRentalsSnapshot.docs.forEach(doc => {
          userRentals.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Query for rentals where consumer is userId
        const userRentalsIdQuery = query(
          collection(db, "offers"),
          where("status", "==", "locked"),
          where("consumerId", "==", user.uid)
        );
        
        const idRentalsSnapshot = await getDocs(userRentalsIdQuery);
        idRentalsSnapshot.docs.forEach(doc => {
          // Only add if not already in the array (avoid duplicates)
          if (!userRentals.some(rental => rental.id === doc.id)) {
            userRentals.push({
              id: doc.id,
              ...doc.data()
            });
          }
        });
        
        let userRentalsData = userRentals;
        
        // Log for debugging
        console.log(`Found ${userRentalsData.length} rentals:`, userRentalsData);
        
        // Fetch provider display names for each rental
        const rentalPromises = userRentalsData.map(async (rental) => {
          try {
            const providerEmail = rental.provider;
            const providerName = await fetchProviderName(providerEmail);
            return {
              ...rental,
              providerName
            };
          } catch (error) {
            console.error(`Error fetching provider name for rental ${rental.id}:`, error);
            return rental;
          }
        });
        
        userRentalsData = await Promise.all(rentalPromises);
        
        setUserBorrows(userRentalsData);
      } catch (rentalsError) {
        console.error("Error fetching user rentals:", rentalsError);
        setUserBorrows([]);
      }
      
      // Fetch user's payment history if they have rentals
      if (userBorrows.length > 0) {
        try {
          const paymentsQuery = query(
            collection(db, "payments"),
            where("userId", "==", user.uid),
            orderBy("timestamp", "desc")
          );
          
          const paymentsSnapshot = await getDocs(paymentsQuery);
          const paymentsData = paymentsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setPaymentHistory(paymentsData);
        } catch (paymentError) {
          console.error("Error fetching payment history:", paymentError);
          setPaymentHistory([]); // Set empty payment history on error
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      // Only set the data that failed to load to empty arrays
      setUserOffers([]);
      setUserBorrows([]);
      // Don't show the toast as it can be disruptive
    } finally {
      clearTimeout(loadingTimeout);
    }
  }, [isAuthenticated, user]);
  
  // Load initial data
  useEffect(() => {
    // Set a timeout to prevent infinite loading state
    const dataLoadingTimeout = setTimeout(() => {
      if (loading) {
        console.log('Data loading timeout reached, resetting loading state');
        setLoading(false);
        toast.error('Loading marketplace data timed out. Please refresh the page.');
      }
    }, 60000); // Increase from 15 seconds to 60 seconds
    
    fetchOffers();
    
    return () => clearTimeout(dataLoadingTimeout);
  }, []); // Remove fetchOffers from dependency array to prevent loop
  
  // Fetch user specific data when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserData();
    } else {
      setUserOffers([]);
      setUserBorrows([]);
    }
  }, [isAuthenticated, user, fetchUserData]);
  
  const handleLogin = () => {
    navigate('/login');
  };
  
  const handleLogout = async () => {
    try {
      await signOut();
      dispatch(clearUser());
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
      toast.error('Failed to logout. Please try again.');
    }
  };
  
  // Helper function to navigate to login with returnTo parameter
  const navigateToLogin = () => {
    const currentPath = window.location.pathname + window.location.search;
    navigate(`/login?returnTo=${encodeURIComponent(currentPath)}`);
  };
  
  const createOffer = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to create an offer');
      navigateToLogin();
      return;
    }
    
    // Validate API key
    if (!newOffer.apiKey.trim()) {
      toast.error('Please enter your Venice API Key');
      return;
    }
    
    try {
      // Validate the API key by checking the VCU balance
      toast.loading('Validating API key...');
      
      const options = {
        method: 'GET', 
        headers: {
          Authorization: `Bearer ${newOffer.apiKey}`
        }
      };

      const apiResponse = await fetch('https://api.venice.ai/api/v1/api_keys/rate_limits', options);
      
      if (!apiResponse.ok) {
        toast.dismiss();
        toast.error('Invalid API key. Please check your Venice API key and try again.');
        return;
      }
      
      const apiData = await apiResponse.json();
      
      // Check if the VCU balance is greater than the daily VCU amount
      const vcuBalance = apiData.data?.balances?.VCU;
      
      if (!vcuBalance || isNaN(vcuBalance)) {
        toast.dismiss();
        toast.error('Could not retrieve VCU balance from the API key.');
        return;
      }
      
      if (vcuBalance < newOffer.dailyVcu) {
        toast.dismiss();
        toast.error(`Your VCU balance (${vcuBalance}) is less than your daily VCU offer (${newOffer.dailyVcu}). Please reduce your offer amount or increase your VCU balance.`);
        return;
      }
      
      const offerData = {
        provider: user?.email,
        dailyVcu: newOffer.dailyVcu,
        minPeriod: newOffer.minPeriod,
        pricePerDay: newOffer.pricePerDay,
        status: 'active',
        totalEarnings: 0,
        apiKey: newOffer.apiKey,
        created: new Date().toISOString()
      };
      
      // Add to Firestore
      const docRef = await addDoc(collection(db, "offers"), offerData);
      
      // Get user displayName or provider name for immediate display
      let providerName = 'Unknown Provider';
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          providerName = userData.providerName || userData.displayName || user.email;
        } else {
          providerName = user.email;
        }
      } catch (err) {
        console.error("Error fetching provider name for new offer:", err);
        providerName = user.email;
      }
      
      // Update local state with proper provider name
      const newOfferWithId = {
        id: docRef.id,
        ...offerData,
        providerName
      };
      
      setOffers(prev => [...prev, newOfferWithId]);
      setUserOffers(prev => [...prev, newOfferWithId]);
      
      setNewOffer({
        dailyVcu: 1000,
        minPeriod: 7,
        pricePerDay: 5.00,
        apiKey: '',
      });
      
      handleTabChange('myOffers');
      toast.success('Offer created successfully!');
    } catch (error) {
      console.error("Error creating offer:", error);
      toast.dismiss();
      toast.error("Failed to create offer. Please try again.");
    }
  };
  
  const openBorrowModal = (offer) => {
    if (!isAuthenticated) {
      toast.error('Please log in to rent VCUs');
      navigateToLogin();
      return;
    }
    
    setSelectedOffer(offer);
    // Set default to minimum period
    setBorrowDays(offer.minPeriod); 
  };
  
  const executeBorrow = () => {
    if (!isAuthenticated) {
      toast.error('Please log in to rent VCUs');
      navigateToLogin();
      return;
    }
    
    // Enforce minimum rental period
    if (borrowDays < selectedOffer.minPeriod) {
      toast.error(`Minimum rental period is ${selectedOffer.minPeriod} days`);
      setBorrowDays(selectedOffer.minPeriod);
      return;
    }
    
    // Calculate total cost
    const totalCost = selectedOffer.pricePerDay * borrowDays;
    
    // Set up checkout details
    setCheckoutDetails({
      offer: selectedOffer,
      days: borrowDays,
      totalCost: totalCost,
      dailyPayment: totalCost / borrowDays,
      startDate: new Date(),
      endDate: new Date(Date.now() + borrowDays * 24 * 60 * 60 * 1000)
    });
    
    // Show checkout confirmation
    setSelectedOffer(null);
    setShowCheckout(true);
  };
  
  // Improved Stripe Checkout integration function
  // Currently not used but keeping for future implementation
  // eslint-disable-next-line no-unused-vars
  const redirectToStripeCheckout = async (amount, metadata) => {
    try {
      // Determine the correct API URL - use relative URL in production
      const isProduction = process.env.NODE_ENV === 'production';
      const apiUrl = isProduction ? '' : (process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || '');
      
      // For production, use relative URL
      const endpointUrl = `${apiUrl}/api/venice/create-checkout-session`;
      
      // Prepare the request data
      const requestData = {
        amount,
        currency: 'usd',
        metadata
      };
      
      console.log('Sending request to:', endpointUrl, 'with data:', requestData);
      
      // Make the API request
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Received successful response:', data);
        
        if (data.url) {
          // Redirect to Stripe Checkout
          window.location.href = data.url;
          return { success: true };
        } else {
          const errorMsg = 'No checkout URL returned from API';
          console.error(errorMsg, data);
          return { 
            success: false, 
            error: errorMsg
          };
        }
      } else {
        // Try to parse error response
        let errorMessage = 'Failed to create checkout session';
        try {
          const errorData = await response.json();
          console.error('API error response:', errorData);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
        }
        
        return { 
          success: false, 
          error: errorMessage
        };
      }
    } catch (error) {
      // Get the API URL again since it might be out of scope in this catch block
      const isProduction = process.env.NODE_ENV === 'production';
      const apiUrl = isProduction ? '' : (process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || '');
      
      // Special handling for 404 errors (API not found)
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('404 Error - API endpoint not found:', `${apiUrl}/api/venice/create-checkout-session`);
        return {
          success: false,
          error: 'API endpoint not found. The server may be down or misconfigured.'
        };
      }
      
      console.error("Error creating Stripe Checkout session:", error);
      return { 
        success: false, 
        error: error.message || 'An unexpected error occurred'
      };
    }
  };
  
  const confirmCheckout = async () => {
    if (!isAuthenticated || !user) {
      toast.error('Please log in to complete this transaction');
      navigateToLogin();
      return;
    }

    try {
      if (paymentMethod === 'credit') {
        // Show loading toast while we process
        toast.loading('Processing payment...', { id: 'credit-payment' });
        
        try {
          // First fetch the latest user data to get the most up-to-date credit balance
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          
          if (!userDoc.exists()) {
            toast.dismiss('credit-payment');
            toast.error('User account not found. Please try again.');
            return;
          }
          
          const userData = userDoc.data();
          const currentCredit = userData.accountCredit || 0;
          
          // Check if user has enough credit
          if (currentCredit < checkoutDetails.totalCost) {
            toast.dismiss('credit-payment');
            toast.error('Insufficient credit. Please add funds or use a credit card.');
            setShowReplenishModal(true);
            return;
          }
          
          // Calculate new balance
          const newCredit = currentCredit - checkoutDetails.totalCost;
          
          // Update the user's credit in Firestore FIRST before updating local state
          await updateDoc(userRef, { 
            accountCredit: newCredit,
            lastUpdated: new Date().toISOString()
          });
          
          // Only update local state after successful Firestore update
          setAccountCredit(newCredit);
          
          // Continue with rental processing
          await finalizeRental();
          toast.dismiss('credit-payment');
        } catch (error) {
          toast.dismiss('credit-payment');
          console.error("Error processing account credit payment:", error);
          toast.error("Failed to process payment. Your balance has not been charged.");
          return;
        }
      } else {
        // Credit card payment flow
        // Show loading toast
        toast.loading('Preparing payment...', { id: 'payment-prep' });
        
        try {
          // Check if the checkoutDetails are valid
          if (!checkoutDetails || !checkoutDetails.offer) {
            toast.dismiss('payment-prep');
            toast.error('Invalid checkout details. Please try again.');
            return;
          }

          // Make sure the offer still exists and is available
          const offerDocRef = doc(db, "offers", checkoutDetails.offer.id);
          const offerDoc = await getDoc(offerDocRef);
          if (!offerDoc.exists()) {
            toast.dismiss('payment-prep');
            toast.error("This offer is no longer available");
            return;
          }
          
          // Determine the correct API URL
          const isProduction = process.env.NODE_ENV === 'production';
          const apiUrl = isProduction ? '' : (process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001');
          
          // Create Stripe Checkout session with all necessary data
          const userIdToken = user.uid; // Use user UID instead of getIdToken
          
          const offerData = offerDoc.data();
          // The API expects amount in dollars (NOT cents) as it multiplies by 100 internally
          const amount = parseFloat(checkoutDetails.totalCost);
          
          // Validate the amount before sending to API
          if (isNaN(amount) || amount <= 0) {
            toast.dismiss('payment-prep');
            toast.error("Invalid amount. Please try again.");
            console.error("Invalid amount for Stripe checkout:", amount);
            return;
          }
          
          console.log("Sending Stripe checkout request with amount in dollars:", amount);
          
          // Clone checkout details to avoid direct mutation
          const checkoutPayload = {
            amount: amount, // Amount in dollars as a number
            offerId: checkoutDetails.offer.id,
            days: checkoutDetails.days,
            currency: 'usd',
            userEmail: user.email,
            metadata: {
              userId: user.uid,
              userEmail: user.email,
              offerId: checkoutDetails.offer.id,
              dailyVcu: offerData.dailyVcu,
              pricePerDay: offerData.pricePerDay,
              days: checkoutDetails.days
            }
          };
          
          console.log("Checkout payload:", checkoutPayload);
          
          const response = await fetch(`${apiUrl}/api/venice/create-checkout-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${userIdToken}`
            },
            body: JSON.stringify(checkoutPayload),
          });

          if (!response.ok) {
            let errorMsg = 'Failed to create checkout session';
            try {
              const errorData = await response.json();
              errorMsg = errorData.error || errorMsg;
            } catch (e) {
              // Parsing error, use default message
            }
            throw new Error(errorMsg);
          }

          const data = await response.json();
          
          if (data.url) {
            // Redirect to Stripe Checkout
            window.location.href = data.url;
          } else {
            throw new Error('No checkout URL returned from API');
          }
          
          toast.dismiss('payment-prep');
        } catch (error) {
          toast.dismiss('payment-prep');
          console.error("Error creating checkout session:", error);
          toast.error(error.message || 'Payment initialization failed. Please try again.');
        }
      }
    } catch (error) {
      console.error("Error processing checkout:", error);
      toast.error("Transaction failed. Please try again.");
    }
  };
  
  // Function to finalize the rental after successful payment
  // NOTE: This function is now deprecated as the webhook should handle this automatically
  // This is kept for backward compatibility or manual intervention if needed
  const finalizeRental = async () => {
    try {
      toast.loading('Finalizing rental...', { id: 'finalizing-rental' });
      
      // Show info that this is not the recommended path
      console.warn("Using manual finalizeRental - the webhook should handle this automatically");
      // toast.warning is not a function in react-hot-toast, use info instead
      toast.error("Using manual rental finalization. This is usually handled automatically.", { 
        duration: 5000,
        icon: '⚠️'
      });
      
      // Update the offer in Firestore
      const offerRef = doc(db, "offers", checkoutDetails.offer.id);
      await updateDoc(offerRef, {
        status: 'locked',
        consumer: user?.email,
        startDate: checkoutDetails.startDate.toISOString(),
        endDate: checkoutDetails.endDate.toISOString()
      });
      
      // Create payment record in Firestore
      const paymentData = {
        offerId: checkoutDetails.offer.id,
        userId: user.uid,
        amount: checkoutDetails.totalCost,
        dailyAmount: checkoutDetails.dailyPayment,
        timestamp: new Date().toISOString(),
        status: 'completed',
        transactionId: 'TXN' + Math.random().toString(36).substring(2, 10),
        method: paymentMethod === 'credit' ? 'Account Credit' : 'Credit Card',
        days: checkoutDetails.days
      };
      
      await addDoc(collection(db, "payments"), paymentData);

      // Create transaction record
      const transactionData = {
        type: 'rental_payment',
        amount: checkoutDetails.totalCost,
        timestamp: new Date().toISOString(),
        userId: user.uid,
        offerId: checkoutDetails.offer.id,
        description: `Rental payment for ${checkoutDetails.days} days`
      };
      
      await addDoc(collection(db, "transactions"), transactionData);
      
      // Update local state
      const updatedOffers = offers.map(o => {
        if (o.id === checkoutDetails.offer.id) {
          return {
            ...o,
            status: 'locked',
            consumer: user?.email,
            startDate: checkoutDetails.startDate.toISOString(),
            endDate: checkoutDetails.endDate.toISOString()
          };
        }
        return o;
      });
      
      setOffers(updatedOffers);
      
      // Dismiss loading toast
      toast.dismiss('finalizing-rental');
      
      // Refresh user data
      await fetchUserData(); // Refresh user's rentals and payment history
      
      // Close modal and show success
      setShowCheckout(false);
      handleTabChange('myRentals');
      toast.success('Rental confirmed successfully!');
    } catch (error) {
      toast.dismiss('finalizing-rental');
      console.error("Error finalizing rental:", error);
      toast.error("Failed to complete transaction. Please try again.");
      
      // If there was an error during rental finalization and we deducted credit,
      // we need to refund the credit back to the user
      if (paymentMethod === 'credit') {
        try {
          // Attempt to refund the deducted amount
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const currentCredit = userData.accountCredit || 0;
            const refundedCredit = currentCredit + checkoutDetails.totalCost;
            
            await updateDoc(userRef, { 
              accountCredit: refundedCredit,
              lastUpdated: new Date().toISOString()
            });
            
            setAccountCredit(refundedCredit);
            toast.success("Your account has been refunded due to the failed transaction.");
          }
        } catch (refundError) {
          console.error("Error refunding credit:", refundError);
          toast.error("Failed to refund your account. Please contact support.");
        }
      }
    }
  };

  // Add this function near the beginning to fetch and set the user's account credit from Firebase
  const fetchUserAccountCredit = useCallback(async () => {
    if (!isAuthenticated || !user?.uid) return;
    
    try {
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const fetchedCredit = userData.accountCredit || 0;
        setAccountCredit(fetchedCredit);
      }
    } catch (error) {
      console.error("Error fetching user account credit:", error);
    }
  }, [isAuthenticated, user?.uid]);

  // Update the useEffect for user authentication to fetch the user's account credit
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserData();
      fetchUserAccountCredit(); // Fetch the user's credit whenever they log in
    } else {
      setUserOffers([]);
      setUserBorrows([]);
    }
  }, [isAuthenticated, user, fetchUserData, fetchUserAccountCredit]);
  
  const handleReplenishCredit = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to add credit');
      navigateToLogin();
      return;
    }
    
    setLoading(true);
    try {
      // Determine the correct API URL
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      // For production, use relative URL
      const endpointUrl = `${apiUrl}/api/venice/add-credit`;
      
      // Create request data
      const requestData = {
        amount: replenishAmount,
        userId: user.uid,
        userEmail: user.email
      };
      
      // Make API request
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}` // Add auth token for verification
        },
        body: JSON.stringify(requestData),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.url) {
          // Redirect to payment page
          window.location.href = data.url;
        } else {
          console.error("No URL in add credit response:", data);
          toast.error('Failed to initialize payment. Please try again later.');
        }
      } else {
        const errorData = await response.json();
        console.error("API response error:", errorData);
        toast.error(errorData.message || 'Failed to add credit');
      }
    } catch (error) {
      console.error("Error adding funds:", error);
      toast.error('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
      setShowReplenishModal(false);
    }
  };
  
  const handleExtendRental = async (rental) => {
    // Set up the selected offer for extension
    setSelectedOffer({
      ...rental,
      extendingRental: true,
      originalEndDate: new Date(rental.endDate)
    });
    
    // Default to 7 more days
    setBorrowDays(7);
  };
  
  // Add this helper function at the top of the component
  const calculateAccruedCost = (rental) => {
    const startDate = new Date(rental.startDate);
    const now = new Date();
    const daysPassed = Math.ceil((now - startDate) / (1000 * 60 * 60 * 24));
    return rental.pricePerDay * daysPassed;
  };

  // Update the displayTotalCost function
  const displayTotalCost = (item) => {
    if (item) {
      if (item.status === 'locked') {
        const cost = calculateAccruedCost(item);
        return formatNumber(item.consumer === user?.email ? cost : -cost);  // Negate if user is consumer
      }
      const cost = item.pricePerDay * item.days;
      return formatNumber(item.consumer === user?.email ? cost : -cost);  // Negate if user is consumer
    }
    if (!selectedOffer) return '0.00';
    return formatNumber(selectedOffer.pricePerDay * borrowDays);
  };

  const calculateDaysRemaining = (rental) => {
    const endDate = new Date(rental.endDate);
    const daysRemaining = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
    return daysRemaining > 0 ? daysRemaining : 0;
  };

  // Add cancelOffer function
  const cancelOffer = async (offerId) => {
    if (!isAuthenticated || !user) {
      toast.error('Please log in to cancel offers');
      navigateToLogin();
      return;
    }
    
    try {
      // Get the current offer
      const offerRef = doc(db, "offers", offerId);
      const offerSnapshot = await getDoc(offerRef);
      
      if (!offerSnapshot.exists()) {
        toast.error("Offer not found");
        return;
      }
      
      const offerData = offerSnapshot.data();
      
      // Different cancellation behavior based on offer status
      let cancellationDate = new Date();
      let statusMessage = '';
      
      if (offerData.status === 'locked') {
        // For rented offers, schedule cancellation for end of day
        const endOfDay = new Date(
          cancellationDate.getFullYear(),
          cancellationDate.getMonth(),
          cancellationDate.getDate(),
          23, 59, 59, 999
        );
        cancellationDate = endOfDay;
        statusMessage = 'Offer cancellation scheduled. It will remain active until 11:59:59 PM today.';
      } else {
        // For non-rented offers, cancel immediately
        statusMessage = 'Offer cancelled successfully.';
      }
      
      // Update the offer in Firestore
      await updateDoc(offerRef, {
        status: offerData.status === 'locked' ? 'cancelling' : 'cancelled',
        cancellationDate: cancellationDate.toISOString(),
        cancelledBy: 'provider',
        cancelledAt: new Date().toISOString()
      });
      
      // Update local state
      const updatedOffers = offers.map(o => {
        if (o.id === offerId) {
          return {
            ...o,
            status: offerData.status === 'locked' ? 'cancelling' : 'cancelled',
            cancellationDate: cancellationDate.toISOString(),
            cancelledBy: 'provider',
            cancelledAt: new Date().toISOString()
          };
        }
        return o;
      });
      
      setOffers(updatedOffers);
      setUserOffers(userOffers.map(o => {
        if (o.id === offerId) {
          return {
            ...o,
            status: offerData.status === 'locked' ? 'cancelling' : 'cancelled',
            cancellationDate: cancellationDate.toISOString(),
            cancelledBy: 'provider',
            cancelledAt: new Date().toISOString()
          };
        }
        return o;
      }));
      
      toast.success(statusMessage);
    } catch (error) {
      console.error("Error cancelling offer:", error);
      toast.error("Failed to cancel offer. Please try again.");
    }
  };

  // Add cancelRental function
  const cancelRental = async (rentalId) => {
    if (!isAuthenticated || !user) {
      toast.error('Please log in to cancel rentals');
      navigateToLogin();
      return;
    }
    
    try {
      // Calculate end of day timestamp (11:59:59 PM today)
      const now = new Date();
      const endOfDay = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23, 59, 59, 999
      );
      
      // Update the rental in Firestore
      const rentalRef = doc(db, "offers", rentalId);
      await updateDoc(rentalRef, {
        status: 'cancelling',
        cancellationDate: endOfDay.toISOString(),
        cancelledBy: 'consumer',
        cancelledAt: new Date().toISOString()
      });
      
      // Update local state
      const updatedBorrows = userBorrows.map(r => {
        if (r.id === rentalId) {
          return {
            ...r,
            status: 'cancelling',
            cancellationDate: endOfDay.toISOString(),
            cancelledBy: 'consumer',
            cancelledAt: new Date().toISOString()
          };
        }
        return r;
      });
      
      setUserBorrows(updatedBorrows);
      
      toast.success('Rental cancellation initiated. It will remain active until 11:59:59 PM today.');
    } catch (error) {
      console.error("Error cancelling rental:", error);
      toast.error("Failed to cancel rental. Please try again.");
    }
  };

  // Add this function to handle payment accrual
  const processPaymentAccrual = async (rental) => {
    try {
      const accruedAmount = calculateAccruedCost(rental);
      const previouslyPaid = rental.lastAccruedAmount || 0;
      const amountToAccrue = accruedAmount - previouslyPaid;

      if (amountToAccrue <= 0) return;

      // Update the offer with new accrued amount
      const offerRef = doc(db, "offers", rental.id);
      await updateDoc(offerRef, {
        lastAccruedAmount: accruedAmount,
        lastAccrualDate: new Date().toISOString()
      });

      // Create payment record for the accrued amount
      await addDoc(collection(db, "payments"), {
        offerId: rental.id,
        userId: rental.provider, // This is the seller's ID
        amount: amountToAccrue,
        timestamp: new Date().toISOString(),
        status: 'completed',
        transactionId: 'ACCRUAL-' + Math.random().toString(36).substring(2, 10),
        method: 'Rental Accrual',
        type: 'seller_payment'
      });

      // Update local state
      const updatedRentals = userBorrows.map(r => {
        if (r.id === rental.id) {
          return {
            ...r,
            lastAccruedAmount: accruedAmount,
            lastAccrualDate: new Date().toISOString()
          };
        }
        return r;
      });
      setUserBorrows(updatedRentals);
    } catch (error) {
      console.error("Error processing payment accrual:", error);
    }
  };

  // Add this effect to process accruals periodically
  useEffect(() => {
    if (!userBorrows.length) return;

    const processAllAccruals = async () => {
      for (const rental of userBorrows) {
        if (rental.status === 'locked') {
          await processPaymentAccrual(rental);
        }
      }
    };

    processAllAccruals();
    const interval = setInterval(processAllAccruals, 24 * 60 * 60 * 1000); // Run daily

    return () => clearInterval(interval);
  }, [userBorrows, processPaymentAccrual]);

  // Add this helper function
  const isUserConsumer = (rental) => {
    return rental.consumer === user?.email;
  };

  // Update the My Rentals display
  const renderTransactionAmount = (amount, isExpense = true) => {
    const displayAmount = isExpense ? amount : -amount;
    return (
      <span className={`${isExpense ? 'text-red-600' : 'text-green-600'}`}>
        {isExpense ? '-' : '+'}${formatNumber(Math.abs(displayAmount))}
      </span>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 w-full">
      {/* Header with enhanced design */}
      <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-blue-800 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <svg className="absolute left-0 top-0 h-full w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="header-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1" />
                <stop offset="50%" stopColor="#2563eb" stopOpacity="0.1" />
                <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            <path fill="url(#header-gradient)" d="M0,100 Q25,0 50,100 Q75,0 100,100 L100,0 L0,0 Z" />
          </svg>
          <svg className="absolute right-0 bottom-0 h-full w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path fill="rgba(255,255,255,0.05)" d="M100,0 Q75,100 50,0 Q25,100 0,0 L0,100 L100,100 Z" />
          </svg>
        </div>
        <div className="container mx-auto px-4 py-4 z-10 relative">
          <div className="flex justify-between items-center">
            <div className="text-white">
              <div className="flex items-center">
                <div>
                  <h1 className="text-xl font-bold">Venice.AI</h1>
                  <p className="text-sm opacity-80">VCU Marketplace</p>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              {isAuthenticated ? (
                <>
                  <div className="text-white mr-4">
                    <span className="font-medium">{user?.email || 'user@example.com'}</span>
                    <div className="text-sm flex items-center">
                      <span>Credit:</span>
                      <span className="ml-1 font-bold bg-blue-800 py-0.5 px-2 rounded">${formatNumber(accountCredit)}</span>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="bg-white text-blue-700 hover:bg-blue-50 text-sm font-medium py-2 px-4 rounded shadow-md flex items-center transition-all"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Log Out
                  </button>
                </>
              ) : (
                <button
                  onClick={handleLogin}
                  className="bg-white text-blue-700 hover:bg-blue-50 text-sm font-medium py-2 px-4 rounded shadow-md flex items-center transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Log In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <main className="flex-grow w-full px-4 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 mb-6 bg-white rounded-t-lg shadow-sm px-4">
            <nav className="flex -mb-px">
              <button
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'home' 
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400' 
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-400'
                }`}
                onClick={() => handleTabChange('home')}
              >
                Home
              </button>
              
              <button
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'marketplace' 
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400' 
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-400'
                }`}
                onClick={() => handleTabChange('marketplace')}
              >
                Marketplace
              </button>
              
              {isAuthenticated && (
                <>
                  <button
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'myOffers' 
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400' 
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-400'
                    }`}
                    onClick={() => handleTabChange('myOffers')}
                  >
                    My Offers
                  </button>
                  
                  <button
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'myBorrows' 
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400' 
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-400'
                    }`}
                    onClick={() => handleTabChange('myRentals')}
                  >
                    My Rentals
                  </button>
                  
                  <button
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'accountSettings' 
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400' 
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-400'
                    }`}
                    onClick={() => handleTabChange('accountSettings')}
                  >
                    Account Settings
                  </button>
                  
                  <div className="ml-4 flex items-center">
                    <ThemeToggle />
                  </div>
                </>
              )}
            </nav>
          </div>
          
          {/* Tab Content */}
          <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow-md p-6 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center p-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading marketplace data...</span>
              </div>
            ) : (
              <>
                {/* Home Tab */}
                {activeTab === 'home' && (
                  <div className="max-w-4xl mx-auto">
                    <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Welcome to the Venice.AI VCU Marketplace</h1>
                    
                    <div className="prose max-w-none dark:prose-invert">
                      <p className="text-lg mb-4 text-gray-700 dark:text-gray-300">
                        The VCU Marketplace is a two-sided marketplace that supports both "renters" and "lenders" of Venice Compute Units (VCUs) to optimize their AI computing resources. Visit Venice.AI to learn more about the Venice API and VCU functionality.
                      </p>
                      
                      <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900 dark:text-white">What are VCUs?</h2>
                      <p className="text-gray-700 dark:text-gray-300">
                        Venice Compute Units (VCUs) are awarded to Venice.AI users who have staked their $VVV tokens, and provide a daily allocation of Venice API credits.
                        By trading VCUs, users can monetize their excess computing capacity or acquire additional resources at a low cost, when needed.
                      </p>
                      
                      <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900 dark:text-white">How it Works</h2>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-6">
                        <div className="bg-blue-50 dark:bg-blue-900 p-6 rounded-lg">
                          <h3 className="text-xl font-semibold mb-2 text-blue-800 dark:text-blue-200">For Providers</h3>
                          <ul className="list-disc pl-5 space-y-2 text-blue-700 dark:text-blue-300">
                            <li>Monetize your excess VCUs</li>
                            <li>Set daily price and minimum rental periods</li>
                            <li>Manage API keys for each offer</li>
                          </ul>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900 p-6 rounded-lg">
                          <h3 className="text-xl font-semibold mb-2 text-green-800 dark:text-green-200">For Consumers</h3>
                          <ul className="list-disc pl-5 space-y-2 text-green-700 dark:text-green-300">
                            <li>Discounted API access</li>
                            <li>Pay with account credit or credit card</li>
                            <li>Get instant access to the provider's API key</li>
                          </ul>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900 p-6 rounded-lg">
                          <h3 className="text-xl font-semibold mb-2 text-purple-800 dark:text-purple-200">Platform Benefits</h3>
                          <ul className="list-disc pl-5 space-y-2 text-purple-700 dark:text-purple-300">
                            <li>Secure payment and escrow systems</li>
                            <li>Open marketplace for lenders and renters</li>
                            <li>Efficient utilization of computing resources</li>
                          </ul>
                        </div>
                      </div>
                      
                      <h2 className="text-2xl font-semibold mt-8 mb-4">Getting Started</h2>
                      <p>
                        To begin using the VCU Marketplace:
                      </p>
                      <ol className="list-decimal pl-5 space-y-2 my-4">
                        <li>Create an account or sign in</li>
                        <li>Browse the marketplace to see available offers</li>
                        <li>As a provider, create your first offer with available VCUs</li>
                        <li>As a consumer, rent VCUs that match your requirements</li>
                        <li>Manage your rentals and offers from your dashboard</li>
                      </ol>
                      
                      <h2 className="text-2xl font-semibold mt-8 mb-4">Configuration of Venice API Keys</h2>
                      <p>
                        To properly configure your Venice API keys for the marketplace:
                      </p>
                      <ol className="list-decimal pl-5 space-y-2 my-4">
                        <li>Go to www.venice.ai/settings/api</li>
                        <li>Click "Generate New API Key"</li>
                        <li>Describe the key, and set the API Key Type to "Inference Only"</li>
                        <li>Do not set an expiration date, users can extend rental periods within the marketplace</li>
                        <li>Set the "Epoch Consumption Limits" to the amount of VCU you are offering within the marketplace</li>
                      </ol>
                      
                      <div className="flex justify-center mt-8">
                        <button
                          onClick={() => handleTabChange('marketplace')}
                          className="px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
                        >
                          Explore the Marketplace
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Marketplace Tab */}
                {activeTab === 'marketplace' && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">Available VCU Offers</h2>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          Showing {offers.filter(o => o.status === 'active').length} active offers
                        </span>
                      </div>
                    </div>
                    
                    {offers.filter(o => o.status === 'active').length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        No active offers available
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Provider
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Daily VCU
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Min Period
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Price (USD/Day)
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Price per VCU
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {offers.filter(o => o.status === 'active').map((offer) => (
                              <tr key={offer.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {offer.providerName || offer.provider}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {offer.dailyVcu ? offer.dailyVcu.toLocaleString() : '0'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {offer.minPeriod || 0} days
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  ${formatNumber(offer.pricePerDay)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  ${formatNumber(offer.pricePerDay / offer.dailyVcu, 4)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                  {isAuthenticated ? (
                                    <button
                                      onClick={() => openBorrowModal(offer)}
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                    >
                                      Rent
                                    </button>
                                  ) : (
                                    <button
                                      onClick={handleLogin}
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
                                    >
                                      Log In
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
                
                {/* My Offers Tab */}
                {activeTab === 'myOffers' && isAuthenticated && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">My VCU Offers</h2>
                      <div className="flex items-center space-x-4">
                        <div>
                          <label htmlFor="totalVcuAllocation" className="block text-sm font-medium text-gray-700 mb-1">
                            My Total VCU Allocation
                          </label>
                          <input
                            type="number"
                            id="totalVcuAllocation"
                            min="1000"
                            step="500"
                            value={totalVcuAllocation}
                            onChange={(e) => setTotalVcuAllocation(parseInt(e.target.value))}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <button
                          onClick={() => handleTabChange('createOffer')}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                          Create New Offer
                        </button>
                      </div>
                    </div>
                    
                    {userOffers.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        You haven't created any offers yet
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Daily VCU
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Min Period
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Price (USD/Day)
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Earnings
                              </th>
                              <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {userOffers.map((offer) => (
                              <tr key={offer.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {offer.dailyVcu ? offer.dailyVcu.toLocaleString() : '0'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {offer.minPeriod || 0} days
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  ${formatNumber(offer.pricePerDay)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    offer.status === 'active' 
                                      ? 'bg-green-100 text-green-800' 
                                      : offer.status === 'locked'
                                        ? 'bg-blue-100 text-blue-800'
                                        : offer.status === 'cancelled' || offer.status === 'cancelling'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {offer.status === 'active' 
                                      ? 'Active' 
                                      : offer.status === 'locked'
                                        ? 'Rented'
                                        : offer.status === 'cancelled'
                                          ? 'Cancelled'
                                          : offer.status === 'cancelling'
                                            ? 'Cancelling'
                                            : offer.status}
                                  </span>
                                  {(offer.status === 'cancelled' || offer.status === 'cancelling' || offer.status === 'locked') && offer.endDate && (
                                    <span className="block text-xs text-gray-500 mt-1">
                                      Until: {new Date(offer.status === 'locked' ? offer.endDate : offer.cancellationDate).toLocaleDateString()}
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  ${formatNumber(offer.totalEarnings)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                  {(offer.status === 'active' || offer.status === 'locked') && (
                                    <button
                                      onClick={() => cancelOffer(offer.id)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      Cancel Offer
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
                
                {/* My Rentals Tab */}
                {activeTab === 'myRentals' && isAuthenticated && (
                  <div>
                    <h2 className="text-xl font-medium mb-4">My Active Rentals</h2>
                    {userBorrows.length === 0 ? (
                      <p className="text-gray-500">You have no active rentals.</p>
                    ) : (
                      <div className="space-y-6">
                        {userBorrows.map(rental => (
                          <div key={rental.id} className="bg-white p-6 rounded-lg shadow">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="text-lg font-medium">VCU API Key Rental</h3>
                                <p className="text-sm text-gray-600">Provider: {rental.providerName || rental.provider}</p>
                                {(rental.status === 'cancelled' || rental.status === 'cancelling') && (
                                  <p className="text-sm mt-1">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      rental.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {rental.status === 'cancelled' ? 'Cancelled' : 'Cancelling'}
                                    </span>
                                    {rental.cancellationDate && (
                                      <span className="text-xs text-gray-500 ml-2">
                                        Until: {new Date(rental.cancellationDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      </span>
                                    )}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-medium">{rental.dailyVcu.toLocaleString()} VCU/day</p>
                                <p className="text-gray-600">${formatNumber(rental.pricePerDay)}/day</p>
                              </div>
                            </div>
                            
                            <div className="mt-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">API Key:</span>
                                <p className="text-sm text-gray-600 mt-1">
                                  API Key: 
                                  <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">*********</span> 
                                </p>
                              </div>
                              
                              <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-medium text-gray-700">Daily VCU Cap:</span>
                                <span className="text-sm">{rental.dailyVcu.toLocaleString()} VCU</span>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-4 gap-4 mt-4 text-sm">
                              <div>
                                <div className="font-medium">Start Date</div>
                                <div>{new Date(rental.startDate).toLocaleDateString()}</div>
                              </div>
                              <div>
                                <div className="font-medium">End Date</div>
                                <div>{new Date(rental.endDate).toLocaleDateString()}</div>
                              </div>
                              <div>
                                <div className="font-medium">Days Remaining</div>
                                <div className="text-blue-600">{calculateDaysRemaining(rental)} days</div>
                              </div>
                              <div>
                                <div className="font-medium">Total Cost</div>
                                <div>${displayTotalCost(rental)}</div>
                              </div>
                            </div>
                            
                            <div className="mt-6">
                              <h4 className="font-medium mb-2">Payment Schedule</h4>
                              <div className="bg-gray-50 rounded-md overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DATE</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AMOUNT</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STATUS</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TRANSACTION ID</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {/* Replace with actual payment data once available */}
                                    {paymentHistory
                                      .filter(payment => payment.offerId === rental.id)
                                      .map((payment, idx) => (
                                        <tr key={idx}>
                                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                            {new Date(payment.timestamp).toLocaleDateString()}
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                            {renderTransactionAmount(payment.amount)}
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                              {payment.status}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{payment.transactionId}</td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            
                            <div className="mt-4 flex justify-between">
                              <button
                                onClick={() => handleExtendRental(rental)}
                                className="mr-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                              >
                                Extend Rental
                              </button>
                              
                              {!(rental.status === 'cancelled' || rental.status === 'cancelling') && (
                                <button
                                  onClick={() => cancelRental(rental.id)}
                                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                                >
                                  Cancel Rental
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Create Offer Tab */}
                {activeTab === 'createOffer' && isAuthenticated && (
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-lg font-medium mb-4">Create a new VCU offer</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Venice API Key</label>
                        <input
                          type="text"
                          value={newOffer.apiKey}
                          onChange={e => setNewOffer({...newOffer, apiKey: e.target.value})}
                          placeholder="<Venice_API_Key>"
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Daily VCU Amount</label>
                        <input
                          type="number"
                          value={newOffer.dailyVcu}
                          onChange={e => setNewOffer({...newOffer, dailyVcu: parseInt(e.target.value)})}
                          placeholder="1000"
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Rental Period (days)</label>
                        <input
                          type="number"
                          value={newOffer.minPeriod}
                          onChange={e => setNewOffer({...newOffer, minPeriod: parseInt(e.target.value)})}
                          placeholder="1"
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Price per Day (USD)</label>
                        <input
                          type="number"
                          value={newOffer.pricePerDay}
                          onChange={e => setNewOffer({...newOffer, pricePerDay: parseFloat(e.target.value)})}
                          placeholder="5.00"
                          className="w-full p-2 border border-gray-300 rounded-md"
                          step="0.01"
                        />
                      </div>
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => handleTabChange('myOffers')}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={createOffer}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                        >
                          Create Offer
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Settings Tab */}
                {activeTab === 'accountSettings' && isAuthenticated && (
                  <AccountSettings inTabView={true} />
                )}
              </>
            )}
          </div>
        </div>
      </main>
      
      {/* Rental Modal */}
      {selectedOffer && (
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Rent VCU API Key
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You are about to rent {selectedOffer.dailyVcu.toLocaleString()} VCU* per day from provider {selectedOffer.provider}.
                        <br /><i>*Note: VCUs are Venice.AI API credits. More information on Venice API VCU pricing can be found at https://docs.venice.ai/overview/pricing. Currently, 1 VCU is equivalent to about $0.10 of daily API credit. </i><br />
                      </p>
                    </div>
                    
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="borrowDays" className="block text-sm font-medium text-gray-700">
                          Rental Period (Days)
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            id="borrowDays"
                            min={selectedOffer.minPeriod}
                            value={borrowDays}
                            onChange={(e) => {
                              const days = parseInt(e.target.value);
                              setBorrowDays(days);
                            }}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Minimum period: {selectedOffer.minPeriod} days
                        </p>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700">Transaction Summary</h4>
                        <div className="mt-2 text-sm">
                          <div className="flex justify-between py-1">
                            <span className="text-gray-500">Daily VCU:</span>
                            <span className="font-medium">{selectedOffer.dailyVcu.toLocaleString()} VCU</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-gray-500">Price Per Day:</span>
                            <span className="font-medium">${selectedOffer.pricePerDay.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-gray-500">Rental Period:</span>
                            <span className="font-medium">{borrowDays} days</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                            <span className="text-gray-500">Total Cost:</span>
                            <span className="font-medium text-blue-600">${displayTotalCost()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={executeBorrow}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Rent Now
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedOffer(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Checkout Confirmation Modal */}
      {showCheckout && (
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-800 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 sm:mx-0 sm:h-10 sm:w-10">
                    <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Payment Confirmation
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-300">
                        You are about to pay ${formatNumber(checkoutDetails?.totalCost)} for {checkoutDetails?.days || 0} days of VCU usage.
                      </p>
                    </div>
                    
                    <div className="mt-4 space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Payment Method</h4>
                        
                        <div className="space-y-3">
                          <div 
                            className={`flex items-center p-3 rounded-md cursor-pointer ${paymentMethod === 'credit' ? 'bg-blue-50 dark:bg-blue-900 border border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                            onClick={() => setPaymentMethod('credit')}
                          >
                            <input
                              type="radio"
                              id="account-credit"
                              name="paymentMethod"
                              checked={paymentMethod === 'credit'}
                              onChange={() => setPaymentMethod('credit')}
                              className="h-4 w-4 text-blue-600 dark:text-blue-500"
                            />
                            <label htmlFor="account-credit" className="ml-3 flex-grow cursor-pointer">
                              <div className="text-sm font-medium dark:text-white">Account Credit</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {accountCredit >= checkoutDetails?.totalCost 
                                  ? `Sufficient funds (${formatNumber(accountCredit)} USD available)` 
                                  : `Insufficient funds (${formatNumber(accountCredit)} USD available)`}
                              </div>
                            </label>
                            <div className="text-sm font-medium dark:text-white">
                              ${formatNumber(Math.min(accountCredit, checkoutDetails?.totalCost || 0))}
                            </div>
                          </div>
                          
                          <div 
                            className={`flex items-center p-3 rounded-md cursor-pointer ${paymentMethod === 'card' ? 'bg-blue-50 dark:bg-blue-900 border border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                            onClick={() => setPaymentMethod('card')}
                          >
                            <input
                              type="radio"
                              id="credit-card"
                              name="paymentMethod"
                              checked={paymentMethod === 'card'}
                              onChange={() => setPaymentMethod('card')}
                              className="h-4 w-4 text-blue-600 dark:text-blue-500"
                            />
                            <label htmlFor="credit-card" className="ml-3 flex-grow cursor-pointer">
                              <div className="text-sm font-medium dark:text-white">Credit Card</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                You'll be redirected to Stripe's secure payment form
                              </div>
                            </label>
                            <div className="text-sm font-medium dark:text-white">
                              ${formatNumber(checkoutDetails?.totalCost)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Payment Information */}
                        {paymentMethod === 'card' && (
                          <div className="mt-2 bg-blue-50 dark:bg-blue-900 p-3 rounded-md text-xs text-blue-700 dark:text-blue-300">
                            <p className="font-medium text-left dark:text-blue-200">What happens next:</p>
                            <ol className="list-decimal pl-5 mt-1 space-y-1 text-left">
                              <li>You'll be redirected to Stripe's secure payment page</li>
                              <li>Enter your card details to complete the payment</li>
                              <li>After successful payment, you'll get immediate access to the API key</li>
                            </ol>
                          </div>
                        )}

                        {paymentMethod === 'credit' && accountCredit < checkoutDetails?.totalCost && (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => setShowReplenishModal(true)}
                              className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Replenish Credit
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Terms of Service</h4>
                        <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1 list-disc pl-5 text-left">
                          <li>Your payment will be processed immediately.</li>
                          <li>You'll receive the API key immediately upon payment confirmation.</li>
                          <li>After the rental period ends, the API key will be deactivated.</li>
                          <li>You may extend the rental period if needed.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmCheckout}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm dark:bg-blue-700 dark:hover:bg-blue-600 dark:focus:ring-blue-500"
                  disabled={paymentMethod === 'credit' && accountCredit < checkoutDetails?.totalCost}
                >
                  Confirm & Pay
                </button>
                <button
                  type="button"
                  onClick={() => setShowCheckout(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Replenish Credit Modal */}
      {showReplenishModal && (
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Add Credit to Your Account
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Add funds to your Venice.AI account to use for VCU rentals.
                      </p>
                    </div>
                    
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="replenishAmount" className="block text-sm font-medium text-gray-700">
                          Amount (USD)
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            id="replenishAmount"
                            min="10"
                            step="10"
                            value={replenishAmount}
                            onChange={(e) => setReplenishAmount(parseFloat(e.target.value))}
                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Current Credit:</span>
                          <span className="text-sm font-medium">${formatNumber(accountCredit)}</span>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-sm text-gray-500">Amount to Add:</span>
                          <span className="text-sm font-medium">${formatNumber(replenishAmount)}</span>
                        </div>
                        <div className="flex justify-between mt-1 pt-1 border-t border-gray-200">
                          <span className="text-sm text-gray-500">New Balance:</span>
                          <span className="text-sm font-medium text-blue-600">${formatNumber(accountCredit + replenishAmount)}</span>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-800">Payment Method</h4>
                        <div className="mt-2 p-3 bg-white rounded-md border border-gray-200">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 mr-3">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-6 w-6">
                                <path d="M22 10v10a1 1 0 01-1 1H3a1 1 0 01-1-1V10h20zm0-2H2V4a1 1 0 011-1h18a1 1 0 011 1v4zm-7 8v-3h-4v3h4z" fill="#6772E5"/>
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Credit Card</p>
                              <p className="text-xs text-gray-500">
                                You'll be redirected to Stripe's secure payment form
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleReplenishCredit}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Add Credit
                </button>
                <button
                  type="button"
                  onClick={() => setShowReplenishModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Database className="text-blue-600 h-6 w-6" />
              <span className="text-gray-600 font-medium">Venice.AI VCU Marketplace</span>
            </div>
            <div className="text-sm text-gray-500">
              All transactions secured with SSL encryption
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VeniceMarketplace;